/**
 * assets/myaiga-config.js
 * ---------------------------------------------------------------------------
 * myAiga✓ — Central configuration layer.
 *
 * This is the ONLY file that should contain default values for the myAiga✓
 * framework. Every other myAiga script reads from `window.myAigaConfig`
 * (built here) instead of hard-coding labels, colours, percentages, URLs,
 * or provider assumptions.
 *
 * Real values come from Shopify theme settings via the tiny inline snippet
 * `snippets/myaiga-bootstrap.liquid`, which sets `window.myAigaSettings`
 * BEFORE this file runs. This file merely normalizes/defaults that object
 * and freezes it into `window.myAigaConfig`.
 *
 * No app names, API endpoints, or DOM structure belonging to any specific
 * third-party membership app are referenced here. Everything is generic and
 * adapter-driven — see myaiga-core.js for the adapter/resolver system.
 * ---------------------------------------------------------------------------
 */
(function (window) {
  "use strict";

  var raw = window.myAigaSettings || {};

  function bool(v, fallback) {
    return typeof v === "boolean" ? v : fallback;
  }
  function str(v, fallback) {
    return typeof v === "string" && v.length ? v : fallback;
  }
  function num(v, fallback) {
    var n = parseFloat(v);
    return isNaN(n) ? fallback : n;
  }

  var config = {
    // -- 1. General -----------------------------------------------------
    enabled: bool(raw.enabled, true),
    membershipName: str(raw.membershipName, "myAiga\u2713"),
    membershipIcon: str(raw.membershipIcon, ""),
    debugMode: bool(raw.debugMode, false),

    // Which layer actually enforces member pricing and the Service Fee waiver.
    //   "theme"    — legacy: this JS mutates the cart and appends ?discount=CODE
    //   "function" — a Shopify Function does both server-side; the theme only
    //                displays what the server already decided.
    // Anything unrecognised falls back to "theme" so a bad setting can never
    // silently switch enforcement off altogether.
    discountEngine: raw.discountEngine === "function" ? "function" : "theme",

    // -- 2. Membership status resolution ---------------------------------
    // See myaiga-core.js for the full adapter list. `source` selects the
    // PRIMARY adapter; `sourcePriority` lets multiple be tried in order.
    membership: {
      source: str(raw.membershipSource, "customer_tag"),
      sourcePriority: Array.isArray(raw.membershipSourcePriority)
        ? raw.membershipSourcePriority
        : [str(raw.membershipSource, "customer_tag")],
      tag: str(raw.membershipTag, "myaiga-member"),
      expiredTag: str(raw.membershipExpiredTag, "myaiga-expired"),
      pendingTag: str(raw.membershipPendingTag, "myaiga-pending"),
      metafieldNamespace: str(raw.membershipMetafieldNamespace, "myaiga"),
      metafieldKey: str(raw.membershipMetafieldKey, "member"),
      customEndpoint: str(raw.membershipCustomEndpoint, ""), // optional custom/API adapter
      treatGuestAsNonMember: bool(raw.treatGuestAsNonMember, true),
    },

    // -- 3. Feature toggles ----------------------------------------------
    features: {
      memberPriceEnabled: bool(raw.memberPriceEnabled, true),
      nonMemberPriceEnabled: bool(raw.nonMemberPriceEnabled, true),
      membershipBadgesEnabled: bool(raw.membershipBadgesEnabled, true),
      productBadgesEnabled: bool(raw.productBadgesEnabled, true),
      collectionBadgesEnabled: bool(raw.collectionBadgesEnabled, true),
      cartMembershipEnabled: bool(raw.cartMembershipEnabled, true),
      checkoutMembershipEnabled: bool(raw.checkoutMembershipEnabled, true),
      accountMembershipEnabled: bool(raw.accountMembershipEnabled, true),
      membershipSignupEnabled: bool(raw.membershipSignupEnabled, true),
      membershipLoginEnabled: bool(raw.membershipLoginEnabled, true),
      hidePlanSelectorForMembers: bool(raw.hidePlanSelectorForMembers, true),
      rewardsEnabled: bool(raw.rewardsEnabled, false),
      manualBadgesEnabled: bool(raw.manualBadgesEnabled, true),
      handlingFeeEnabled: bool(raw.handlingFeeEnabled, false),
    },

    // -- 4. Pricing engine -------------------------------------------------
    pricing: {
      // priority order, first match wins per-product:
      // ["product_metafield","variant_metafield","app","function","percentage","fixed"]
      sourcePriority: Array.isArray(raw.pricingSourcePriority)
        ? raw.pricingSourcePriority
        : ["variant_metafield", "product_metafield", "percentage"],
      memberDiscountPercent: num(raw.memberDiscountPercent, 20),
      nonMemberMarkupPercent: num(raw.nonMemberMarkupPercent, 0),
      currency: str(raw.currency, ""), // blank = use Shopify's active currency
      metafieldNamespace: str(raw.pricingMetafieldNamespace, "myaiga"),
    },

    // -- 5. Handling fee ---------------------------------------------------
    handlingFee: {
      enabled: bool(raw.handlingFeeEnabled, false),
      amount: num(raw.handlingFeeAmount, 0),
      memberFee: num(raw.memberHandlingFee, 0),
      nonMemberFee: num(raw.nonMemberHandlingFee, num(raw.handlingFeeAmount, 0)),
      source: str(raw.handlingFeeSource, "theme"), // theme | theme_product_removal | function | app
      serviceFeeVariantId: raw.serviceFeeVariantId ? String(raw.serviceFeeVariantId) : null,
      discountMarkerVariantId: raw.discountMarkerVariantId ? String(raw.discountMarkerVariantId) : null,
      serviceFeeAutoReadd: bool(raw.serviceFeeAutoReadd, false),
    },

    aiTrillionMembership: raw.aiTrillionMembership || {},

    // -- 5b. Checkout discount code (real checkout enforcement) ------------
    checkoutDiscount: {
      enabled: bool(raw.checkoutDiscountCodeEnabled, false),
      code: str(raw.checkoutDiscountCode, ""),
      codeMonthly: str(raw.checkoutDiscountCodeMonthly, ""),
      codeYearly: str(raw.checkoutDiscountCodeYearly, ""),
      planDiscountCodes: (raw.planDiscountCodes && typeof raw.planDiscountCodes === "object") ? raw.planDiscountCodes : {},
      monthlyCode: str(raw.checkoutDiscountCodeMonthly, ""),
      yearlyCode: str(raw.checkoutDiscountCodeYearly, ""),
      memberCode: str(raw.checkoutDiscountCode, ""),
      applyTo: str(raw.checkoutDiscountApplyTo, "member"), // member | everyone
      excludePlans: bool(raw.checkoutDiscountExcludePlans, true),
      applyOnPlanPurchase: true,
      planVariantIds: Array.isArray(raw.planVariantIds)
        ? raw.planVariantIds.filter(Boolean).map(String)
        : [],
    },

    // -- 6. Labels (all copy lives here, never inline in templates) -------
    labels: {
      memberPrice: str(raw.labelMemberPrice, raw.membershipName ? raw.membershipName + " Price" : "myAiga\u2713 Price"),
      nonMemberPrice: str(raw.labelNonMemberPrice, "Don't Pay"),
      savings: str(raw.labelSavings, "Save"),
      badgeText: str(raw.labelBadgeText, "myAiga\u2713 Member"),
      joinButton: str(raw.labelJoinButton, "Join myAiga\u2713"),
      loginButton: str(raw.labelLoginButton, "Log in"),
      learnMoreButton: str(raw.labelLearnMoreButton, "Learn more"),
      manageButton: str(raw.labelManageButton, "Manage membership"),
      memberConfirmed: str(raw.labelMemberConfirmed, "\u2713 myAiga\u2713 Member Price"),
      nonMemberUpsell: str(raw.labelNonMemberUpsell, "Join myAiga\u2713 and save"),
      noHandlingFee: str(raw.labelNoHandlingFee, "No handling fee"),
      handlingFeeApplies: str(raw.labelHandlingFeeApplies, "Handling fee applies"),
    },

    // -- 7. Navigation / account URLs (never hard-coded elsewhere) --------
    urls: {
      signup: str(raw.signupUrl, "/pages/myaiga"),
      login: str(raw.loginUrl, "/account/login"),
      account: str(raw.accountUrl, "/account"),
      manage: str(raw.manageUrl, "/account"),
      learnMore: str(raw.learnMoreUrl, "/pages/myaiga"),
      rewards: str(raw.rewardsUrl, ""),
    },

    // -- 8. Design tokens (mirrors CSS variables in myaiga.css) -----------
    design: {
      primary: str(raw.colorPrimary, "#d11a29"),
      secondary: str(raw.colorSecondary, "#111111"),
      savings: str(raw.colorSavings, "#d11a29"),
      badgeText: str(raw.badgeTextColor, "#ffffff"),
      badgeBackground: str(raw.badgeBackgroundColor, "#d11a29"),
      radius: str(raw.borderRadius, "6px"),
    },

    // -- 9. Runtime / perf --------------------------------------------------
    runtime: {
      cacheTtlMs: num(raw.cacheTtlMs, 5 * 60 * 1000),
      pollingEnabled: bool(raw.pollingEnabled, false),
    },
  };

  // Freeze one level deep to discourage ad-hoc mutation from feature code;
  // use window.myAiga.refresh()/events to change state instead.
  try {
    Object.keys(config).forEach(function (k) {
      if (config[k] && typeof config[k] === "object") Object.freeze(config[k]);
    });
    Object.freeze(config);
  } catch (e) {
    /* older browsers: non-fatal */
  }

  window.myAigaConfig = config;
})(window);
